import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import MainURL from '../../../MainURL';
import { getAdminSession } from '../../adminSession';
import '../../Admin.scss';

const CATEGORIES = ['공지', '업데이트', '행사'] as const;

type Notice = {
  id: number;
  category: string;
  title: string;
  content: string;
  isPublished: 0 | 1;
  isPinned: 0 | 1;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

type ApiEnvelope<T> = { ok: boolean; data?: T; message?: string };

function formatDateTime(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const min = String(date.getMinutes()).padStart(2, '0');
  return `${yyyy}.${mm}.${dd} ${hh}:${min}`;
}

function errorMessageOf(err: unknown, fallback: string): string {
  const data = axios.isAxiosError(err) ? err.response?.data : null;
  if (data && typeof data === 'object' && 'message' in data) {
    return String((data as { message?: string }).message) || fallback;
  }
  return fallback;
}

const EMPTY_FORM = { category: '공지', title: '', content: '', isPublished: false, isPinned: false };

export default function NoticeAuthoring() {
  const session = getAdminSession();
  const [items, setItems] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  const fetchNotices = useCallback(async () => {
    setLoading(true);
    setErrorMsg('');
    try {
      const res = await axios.get<ApiEnvelope<Notice[]>>(`${MainURL}/admin-console/notices`);
      if (res.data?.ok && res.data.data) {
        setItems(res.data.data);
      } else {
        setErrorMsg(res.data?.message || '공지사항 목록을 불러오지 못했습니다.');
      }
    } catch (err) {
      setErrorMsg(errorMessageOf(err, '공지사항 목록을 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const startEdit = (notice: Notice) => {
    setEditingId(notice.id);
    setForm({
      category: notice.category || '공지',
      title: notice.title,
      content: notice.content,
      isPublished: Boolean(notice.isPublished),
      isPinned: Boolean(notice.isPinned),
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!session) return;
    if (!form.title.trim() || !form.content.trim()) {
      alert('제목과 내용을 입력해 주세요.');
      return;
    }
    setSaving(true);
    try {
      if (editingId) {
        const res = await axios.put<ApiEnvelope<null>>(`${MainURL}/admin-console/notices/${editingId}`, {
          requesterId: session.id,
          ...form,
        });
        if (!res.data?.ok) throw new Error(res.data?.message);
      } else {
        const res = await axios.post<ApiEnvelope<null>>(`${MainURL}/admin-console/notices`, {
          requesterId: session.id,
          ...form,
        });
        if (!res.data?.ok) throw new Error(res.data?.message);
      }
      cancelEdit();
      fetchNotices();
    } catch (err) {
      alert(errorMessageOf(err, '저장하지 못했습니다.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!session) return;
    if (!window.confirm('이 공지사항을 삭제하시겠습니까?')) return;
    try {
      const res = await axios.delete<ApiEnvelope<null>>(`${MainURL}/admin-console/notices/${id}`, {
        params: { requesterId: session.id },
      });
      if (res.data?.ok) {
        setItems((prev) => prev.filter((item) => item.id !== id));
      } else {
        alert(res.data?.message || '삭제하지 못했습니다.');
      }
    } catch (err) {
      alert(errorMessageOf(err, '삭제하지 못했습니다.'));
    }
  };

  return (
    <div className="Main-cover admin-panel">
      <div className="admin-panel-header">
        <div>
          <h2 className="admin-panel-title">공지사항 관리</h2>
          <p className="admin-panel-desc">
            회사소개 &gt; 공지사항 페이지(/company/notice)에 보일 공지를 등록·수정합니다. 발행된 공지만 공개됩니다.
          </p>
        </div>
        <button type="button" className="admin-refresh-btn" onClick={fetchNotices} disabled={loading}>
          {loading ? '불러오는 중...' : '새로고침'}
        </button>
      </div>

      <form className="admin-form" onSubmit={handleSubmit}>
        <select
          value={form.category}
          onChange={(e) => setForm((prev) => ({ ...prev, category: e.target.value }))}
        >
          {CATEGORIES.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select>
        <input
          type="text"
          placeholder="제목"
          value={form.title}
          onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
        />
        <textarea
          placeholder="내용"
          value={form.content}
          onChange={(e) => setForm((prev) => ({ ...prev, content: e.target.value }))}
          rows={6}
        />
        <label className="admin-form-checkbox">
          <input
            type="checkbox"
            checked={form.isPublished}
            onChange={(e) => setForm((prev) => ({ ...prev, isPublished: e.target.checked }))}
          />
          발행 (공지사항 페이지에 공개)
        </label>
        <label className="admin-form-checkbox">
          <input
            type="checkbox"
            checked={form.isPinned}
            onChange={(e) => setForm((prev) => ({ ...prev, isPinned: e.target.checked }))}
          />
          상단 고정
        </label>
        <div className="admin-form-actions">
          <button type="submit" className="admin-refresh-btn" disabled={saving}>
            {editingId ? '수정 저장' : '공지 등록'}
          </button>
          {editingId && (
            <button type="button" className="admin-cancel-btn" onClick={cancelEdit}>
              취소
            </button>
          )}
        </div>
      </form>

      {errorMsg && <p className="admin-error-text">{errorMsg}</p>}

      <div className="table-container">
        <table className="admin-table">
          <thead>
            <tr>
              <th>작성일</th>
              <th>분류</th>
              <th>제목</th>
              <th>작성자</th>
              <th>상태</th>
              <th>관리</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && !loading ? (
              <tr>
                <td colSpan={6}>등록된 공지사항이 없습니다.</td>
              </tr>
            ) : (
              items.map((item) => (
                <tr key={item.id}>
                  <td>{formatDateTime(item.createdAt)}</td>
                  <td>{item.category || '공지'}</td>
                  <td style={{ textAlign: 'left' }}>
                    {item.isPinned ? <strong>[고정] </strong> : null}
                    {item.title}
                  </td>
                  <td>{item.createdBy || '-'}</td>
                  <td>
                    <span className={`admin-status-tag admin-status-tag--${item.isPublished ? 'signed' : 'pending'}`}>
                      {item.isPublished ? '발행됨' : '비공개'}
                    </span>
                  </td>
                  <td>
                    <button type="button" className="admin-btn edit-btn" onClick={() => startEdit(item)}>
                      수정
                    </button>
                    <button type="button" className="admin-btn delete-btn" onClick={() => handleDelete(item.id)}>
                      삭제
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
